import React from 'react';
import { MedicalDataTypes } from '../../../../../primary-record-types';
import useMedicalProfileData from '../../../../../hooks/api/useMedicalProfileData';
import { getIntl } from '../../../../../utils/intl';
import useIdList from '../../../../../hooks/useIdList';
import { pluralResourceCategoryMessages, resourceCategoryMessages } from '../../../../../utils/sharedMessages';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import ResourceSearch from '../../../../shared/ResourceSearch/ResourceSearch';
import ResourceListCard from '../../../../shared/ResourceSearch/ResourceListCard';
import useCareTeamMemberResources from '../../../../../hooks/api/useCareTeamMemberResources';
import { ListMode, useListEditActions } from '../../../../../hooks/useListEditActions';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { ContentStack } from '../../../../v2BaseComponents/Stack';

const intl = getIntl('careTeamMemberResources', {
    add: 'Add',
    link: 'Link',
    cancel: 'Cancel',
    save: 'Save',
    updateMessage: 'add {add} and remove {remove}',
});

type CareTeamMemberResourcesProps = {
    profileId: string;
    resourceCategory: MedicalDataTypes.PrimaryRecordResourceCategory;
    careTeamMemberId: string;
    relatedResources: MedicalDataTypes.PrimaryRecordResource[];
    isLoadingRelatedResources: boolean;
    hasEditPermission: boolean;
};

const CareTeamMemberResources: React.FC<CareTeamMemberResourcesProps> = ({
    profileId,
    resourceCategory,
    careTeamMemberId,
    relatedResources,
    isLoadingRelatedResources,
    hasEditPermission,
}) => {
    const { idList, addId, removeId, updateMessage, resetIdList } = useIdList({
        initialIdList: relatedResources.map((resource) => resource.id),
        getUpdateMessage: ({ addCount, removeCount, isAddPlural, isRemovePlural }) => {
            const getResourceMessage = (
                isPlural: boolean,
                count: number,
                category: MedicalDataTypes.PrimaryRecordResourceCategory,
            ) => {
                const resourceMessage = !isPlural
                    ? resourceCategoryMessages(category)
                    : pluralResourceCategoryMessages(category);
                return `${count} ${resourceMessage}`;
            };

            return intl('updateMessage', {
                add: getResourceMessage(isAddPlural, addCount, resourceCategory).toLocaleLowerCase(),
                remove: getResourceMessage(isRemovePlural, removeCount, resourceCategory).toLocaleLowerCase(),
            });
        },
    });
    const profileNavigation = useProfilesTabNavigation();
    const [listMode, setListMode] = React.useState<ListMode>('related');

    const { groupedResources, isLoadingResources } = useMedicalProfileData({
        medicalProfileId: profileId,
        resourceCategory,
    });
    const { updateCareTeamMemberResources, isUpdatingCareTeamMemberResources } = useCareTeamMemberResources({
        medicalProfileId: profileId,
        careTeamMemberId,
        resourceCategories: [resourceCategory],
        onUpdateCareTeamMemberResourcesSuccess: () => setListMode('related'),
    });
    const listEditActions = useListEditActions({
        canEdit: hasEditPermission,
        listMode,
        onEdit: () => setListMode('link'),
        onCancel: () => {
            setListMode('related');
            resetIdList();
        },
        onAdd: () => {
            profileNavigation.navigate('add-data', {
                profileId,
                category: resourceCategory,
                sourceResourceId: null,
                careTeamMemberId,
            });
        },
        onSubmitLinkUpdates: () => {
            updateCareTeamMemberResources({
                resourceCategory,
                resourceIds: idList,
            });
        },
        isDisabled: isUpdatingCareTeamMemberResources,
        promptChangeDescription: updateMessage,
    });

    const renderList = () => {
        if (listMode === 'related')
            return (
                <ResourceSearch
                    linkEmptyStateHint
                    category={resourceCategory}
                    resources={relatedResources}
                    isLoadingResources={isLoadingRelatedResources}
                    disableDetailedSearch
                    renderFilteredResources={(resource) => (
                        <ResourceListCard
                            isSelectable={false}
                            resource={resource}
                            category={resourceCategory}
                            profileId={profileId}
                            onNavigateToDetails={() =>
                                profileNavigation.navigate('data-details', {
                                    profileId,
                                    category: resourceCategory,
                                    resourceId: resource.id,
                                })
                            }
                        />
                    )}
                />
            );
        return (
            <ResourceSearch
                linkEmptyStateHint
                category={resourceCategory}
                resources={groupedResources}
                isLoadingResources={isLoadingResources}
                renderFilteredResources={(resource) => {
                    const isSelected = idList.includes(resource.id);
                    return (
                        <ResourceListCard
                            isSelectable={true}
                            resource={resource}
                            category={resourceCategory}
                            profileId={profileId}
                            isDisabled={isUpdatingCareTeamMemberResources}
                            isSelected={isSelected}
                            onSelect={() => {
                                isSelected ? removeId(resource.id) : addId(resource.id);
                            }}
                        />
                    );
                }}
            />
        );
    };

    return (
        <>
            <AnimateLoadedContent isLoading={isLoadingResources || isUpdatingCareTeamMemberResources}>
                {() => (
                    <ActionButtonLayout actions={listEditActions.actions}>
                        <ContentStack>{renderList()}</ContentStack>
                    </ActionButtonLayout>
                )}
            </AnimateLoadedContent>
            {listEditActions.Prompt}
        </>
    );
};

export default React.memo(CareTeamMemberResources);
